import { DispatcherEvent } from '@carvana/merch-native/index';
import cx from 'classnames';
import { useContext } from 'react';
import { PrimaryButton, SecondaryButton } from '@/common/Button';
import TrackWrapper from '@/common/TrackWrapper';
import {
  activeTradeStatuses,
  TradeStatus,
  getTradeCtaDetails
} from '@/common/TradeCard/constants';
import DefaultTradeCard from '@/common/TradeCard/DefaultTradeCard';
import TradeHeading from '@/common/TradeCard/TradeHeading';
import { fireAction } from '@/home/HeroCardsReturnUser/Cards/actionNames';
import LoadingCard, {
  CardType
} from '@/home/HeroCardsReturnUser/Cards/LoadingCard';
import useHasPurchaseInProgress from '@/home/hooks/useHasPurchaseInProgress';
import formatEvent from '@/home/utils/formatEvent';
import COOKIES from '~/constants/cookies';
import Sections from '~/constants/home/sections';
import TradeContext, { useIsTradeLoaded } from '~/context/common/TradeContext';
import { useGetUserPurchaseIsLoaded } from '~/context/home/UserPurchaseContext';
import { TransactionIntent } from '~/contracts/common/Trade';
import useGetTradeInStatus from '~/hooks/common/useGetTradeStatus';
import { nativeSrpPayload, nativeStcPayload } from '~/native/common/utils';
import sendMessageToNative from '~/native/home/dispatchers/sendMessageToNative';
import BrowserCookies from '~/utilities/BrowserCookies';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';
import TradeDetails from './TradeDetails';

function TradeCard() {
  const { selectedTrade } = useContext(TradeContext);
  const {
    transactionIntent = TransactionIntent.Unknown,
    daysToExpiration = 0,
    id = ''
  } = selectedTrade ?? {};
  const hasPurchaseInProgress = useHasPurchaseInProgress();
  const tradeStatus = useGetTradeInStatus();
  const hasTrade = tradeStatus !== TradeStatus.None;
  const nativePayloadToUse = activeTradeStatuses[tradeStatus]
    ? nativeSrpPayload
    : nativeStcPayload;
  const { ctaText, ctaHref } = getTradeCtaDetails(
    tradeStatus,
    transactionIntent,
    id
  );

  const commonCustomAttributes = {
    description: 'STC card',
    expireDays: daysToExpiration,
    status: tradeStatus === TradeStatus.None ? 'default' : tradeStatus,
    cta: ctaText
  };

  const hideCTA =
    (hasPurchaseInProgress &&
      [TradeStatus.Active, TradeStatus.ExpiringSoon].includes(tradeStatus)) ||
    tradeStatus === TradeStatus.Pending;

  const ctaProps = {
    dataAnalyticsId: 'Home feed card',
    dataAnalyticsAttributes: JSON.stringify({
      ...commonCustomAttributes,
      item: 'cta button'
    }),
    dataNativePayload: nativePayloadToUse,
    dataQa: 'trade-card-cta',
    href: ctaHref,
    onClick: () => {
      fireAction(CardType.Trade);
      BrowserCookies.remove(COOKIES.tradeInApplied);
      sendMessageToNative(DispatcherEvent.ToggleTradeIn, {
        isTradeInApplied: true
      });
    }
  };

  const CtaButton =
    tradeStatus === TradeStatus.Expired ? PrimaryButton : SecondaryButton;

  return hasTrade ? (
    <TrackWrapper
      event={formatEvent('Viewed - Home feed card', commonCustomAttributes)}
      trackOnce
      key={tradeStatus}
    >
      <div
        className={cx(
          'flex flex-col rounded-[6px] border border-solid border-grey-2 p-2xl text-blue-6 -:bg-white m:h-[216px] l:h-[211px]',
          hideCTA ? 'h-auto gap-y-2xl' : 'h-[240px] justify-between'
        )}
      >
        <TradeHeading />
        <TradeDetails ctaText={ctaText} />
        <a
          className={hideCTA ? 'hidden' : ''}
          data-analytics-id={ctaProps.dataAnalyticsId}
          data-analytics-attributes={ctaProps.dataAnalyticsAttributes}
          data-native-payload={ctaProps.dataNativePayload}
          data-qa={ctaProps.dataQa}
          href={ctaProps.href}
          onClick={ctaProps.onClick}
        >
          <CtaButton className="h-40 min-h-40 w-full">{ctaText}</CtaButton>
        </a>
      </div>
    </TrackWrapper>
  ) : (
    <DefaultTradeCard ctaProps={ctaProps} />
  );
}

function TradeCardWrapper() {
  const isUserPurchaseLoaded = useGetUserPurchaseIsLoaded();
  const isTradesLoaded = useIsTradeLoaded();

  return isTradesLoaded && isUserPurchaseLoaded ? (
    <TradeCard />
  ) : (
    <LoadingCard card={CardType.Trade} />
  );
}

export default withErrorBoundary(
  TradeCardWrapper,
  undefined,
  undefined,
  Sections.TradeCard
);
